<template>
  <section class="section">
    <h1 class="title is-size-5 has-text-color-7 has-text-weight-semibold mb-25">
      {{ General.PERMISSIONS }}
    </h1>
    <section>
      <b-input
        icon="magnify"
        :placeholder="General.TYPE_IT_SEARCH"
        @input="setSearchText"
      />

      <div class="level-left has-text-left m-10">
        <span class="has-text-color-7 has-text-weight-semibold">
          {{ getPaginationDetails }}
        </span>
      </div>

      <b-table
        v-if="observerPage"
        class="custom-table-administration custom-3-columns-adm"
        :data="isLoading ? [] : getPermission.list"
        :sortIcon="sortIcon"
        :sortIconSize="sortIconSize"
        :aria-next-label="General.NEXT_PAGE"
        :aria-previous-label="General.PREVIOUS_PAGE"
        :aria-page-label="General.PAGE"
        :aria-current-label="General.CURRENT_PAGE"
        :defaultSort="sortOrder"
        :defaultSortDirection="defaultSortOrder"
        backendSorting
        type="is-black"
        hoverable
        @sort="onSort"
      >
        <template>
          <b-table-column
            v-slot="props"
            :label="General.PERMISSION"
            field="name"
            :sortable="!isMobile"
          >
            <span class="custom-break has-text-color-7 has-text-left">
              {{ props.row.name }}
            </span>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="General.PROFILES"
            field="profile"
            :sortable="!isMobile"
          >
            <b-button
              class="select-button"
              :class="{ 'is-align-items-flex-start': isMobile }"
              :disabled="!props.row.profiles_count"
              tag="router-link"
              :to="goToSearchProfiles(props.row)"
            >
              {{ props.row.profiles_count }}
              {{
                props.row.profiles_count === 1
                  ? General.PROFILE
                  : General.PROFILES
              }}
            </b-button>
          </b-table-column>
          <b-table-column
            v-slot="props"
            :label="isMobile ? '' : General.ACTIONS"
          >
            <ActionsGrouper :actions="actionList(props.row)" />
          </b-table-column>
        </template>

        <template slot="empty">
          <section class="section">
            <NoListMessage v-if="!isLoading" />
            <b-loading v-else :isFullPage="false" :active="isLoading" />
          </section>
        </template>

        <template slot="footer">
          <div class="pagination-custom">
            <b-dropdown
              v-model="perPage"
              aria-role="list"
              position="is-top-right"
              :expanded="isMobile"
            >
              <template slot="trigger">
                <b-button class="custom-dropdown-button" :expanded="isMobile">
                  <span class="is-flex">
                    {{ perPage }} {{ General.PER_PAGE.toLowerCase() }}
                    <b-icon class="spacing-mx-5" icon="chevron-down" />
                  </span>
                </b-button>
              </template>
              <div class="spacing-p-5 has-text-centered">
                <span class="has-text-color-7">
                  {{ General.RECORDS_FOR_PAGE }}
                </span>
                <div class="list-page">
                  <b-dropdown-item
                    v-for="option in optionsPagination"
                    :key="option"
                    :value="option"
                  >
                    <div>
                      {{ option }}
                    </div>
                  </b-dropdown-item>
                </div>
              </div>
            </b-dropdown>
            <b-pagination
              :total="getPermission.count"
              :perPage="perPage"
              :current.sync="currentPage"
              rangeBefore="1"
              rangeAfter="1"
              order="is-centered"
              class="process-panel-pagination"
              rounded
              :iconPrev="prevIcon"
              :iconNext="nextIcon"
              :aria-next-label="General.NEXT_PAGE"
              :aria-previous-label="General.PREVIOUS_PAGE"
              :aria-page-label="General.PAGE"
              :aria-current-label="General.CURRENT_PAGE"
            />
          </div>
        </template>
      </b-table>
    </section>
  </section>
</template>

<script>
import { App, General } from "@/utils/constants";
import { mapActions, mapGetters } from "vuex";
import NoListMessage from "@/components/NoListMessage";
import debounce from "lodash/debounce";
import { formatPagination } from "@/utils/transformers";

import { TAB_PERSIST_NAME as PERSIST_NAME } from "@/utils/enumerate";
import { setPersistSort } from "@/plugins/indexDB";

export default {
  name: "PermissionList",
  components: {
    NoListMessage
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      observerPage: true,
      perPage: 10,
      currentPage: 1,
      prevIcon: "chevron-left",
      nextIcon: "chevron-right",
      sortField: "name",
      sortOrder: ["name", "asc"],
      sortIcon: "menu-up",
      sortIconSize: "is-medium",
      defaultSortOrder: "desc",
      search: ""
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      optionsPagination: "getItensPage",
      getPermission: "getPermission",
      isMobile: "isMobile"
    }),
    getPaginationDetails() {
      return formatPagination(
        this.getPermission,
        this.currentPage,
        this.perPage
      );
    },
    requestBaseData() {
      return {
        page: this.currentPage,
        paginator_count: this.perPage,
        search_text: this.search,
        order_by: this.sortField,
        simple: true
      };
    },

    tabPersistName() {
      return `${PERSIST_NAME.PERMISSIONS}_${this.user.id}`;
    }
  },

  watch: {
    requestBaseData() {
      this.getAllData();
    }
  },

  async beforeMount() {
    this.observerPage = false;
    const defaultSort = JSON.stringify({
      [this.sortField]: this.defaultSortOrder
    });
    this.sortOrder = await this.getSortPersist({
      sortField: defaultSort,
      tabPersistName: this.tabPersistName
    });
    this.observerPage = true;
    this.onSort(...this.sortOrder);

    this.getAllData();
  },

  methods: {
    ...mapActions({
      getAllPermissions: "getAllPermissionsRequest",
      getSortPersist: "getPersistSortAction"
    }),
    onSort(field, order) {
      this.sortField = order !== this.defaultSortOrder ? field : `-${field}`;
      setPersistSort({
        name: this.tabPersistName,
        sort: { [field]: order }
      });
    },
    edit(permissionId) {
      return `/app/admin/permissao/${permissionId}`;
    },
    setSearchText: debounce(function(value) {
      this.currentPage = 1;
      this.search = value;
    }, 500),
    getAllData: debounce(async function() {
      this.isLoading = true;
      if (this.observerPage) await this.getAllPermissions(this.requestBaseData);
      this.isLoading = false;
    }, 1000),
    goToSearchProfiles(permission) {
      return {
        name: "Perfis",
        params: {
          currentFilterList: [
            {
              data: [{ id: permission.id, name: permission.name }],
              type: "PERMISSIONS"
            }
          ]
        }
      };
    },
    actionList(permission) {
      return [
        {
          label: this.General.EDIT,
          icon: "square-edit-outline",
          link: this.edit(permission.id)
        }
      ];
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";
.section {
  background-color: $color-1;
  padding-top: 20px;

  .select-button {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    text-decoration: underline;
    color: $color-7;
  }
}

.table-profiles {
  max-width: 81vw;
  @media screen and (max-width: $tablet) {
    max-width: 95vw;
  }
  th div {
    width: 110px;
    text-align: center;
  }
  .large-column {
    width: 220px;
    display: flex;
  }
  .centered-custom {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: calc(0.75em - 1px);
  }
}
</style>

<style lang="scss">
.table-profiles {
  th div {
    width: 110px;
    text-align: center;
    justify-content: center;
  }
  .vertical-middle {
    vertical-align: middle;
  }
}
</style>
